/* Pure preference/ranking helpers shared by the storefront and regression tests. */
(function (root) {
  'use strict';
  const finish = value => {
    const s = String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    if (/platead|plata|argent|silver|silber|zilver|srebr|srebro|ezust|hopea|silvr|palladium|or blanc|white gold|oro bianco|weißgold|weissgold|witgoud|biale zloto|feherarany|valkokulta|vitt guld/.test(s)) return 'silver';
    if (/(^or\b|gold|dore|oro|goud|złot|zlot|arany|kulta|kullan|pozłac|pozlac|guld|dorad)/.test(s)) return 'gold';
    return null;
  };
  const ids = p => [...new Set([p.product_id, p.id, ...(p._allVariantIds || []),
    ...Object.values(p.variants || {}).map(v => typeof v === 'object' ? v.id : v),
    ...Object.values(p.sizes || {}).flatMap(s => Object.values(s).map(v => v.id))].filter(Boolean).map(String))];
  const favorite = (p, favorites) => ids(p).some(id => Boolean(favorites[id]));
  // Stable Shopify sales order within each group. A selected jewel is not a favorite.
  function salesOrder(list, favorites = {}) {
    return list.map((product, index) => ({product, index}))
      .sort((a, b) => Number(favorite(b.product, favorites)) - Number(favorite(a.product, favorites)) ||
        (a.product._rank ?? a.index) - (b.product._rank ?? b.index) || a.index - b.index)
      .map(entry => entry.product);
  }
  function composerOrder(list, favorites = {}) {
    const family = product => {
      const type = String(product.type_de_bijoux || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
      if (/anneau|creole|hoop|boucle d[’']oreille/.test(type)) return 0;
      return 1;
    };
    return salesOrder(list, favorites).sort((a, b) => family(a) - family(b));
  }
  function isStudLike(product) {
    return /\b(?:studs?|puces?)\b/i.test(String(product?.type_de_bijoux || ''));
  }
  function alternate(list, viewed, favorites = {}, selected = () => false) {
    const pinned = [], known = [], fresh = [], seen = new Set();
    for (const p of list) {
      const key = String(p.product_id || p.id);
      if (seen.has(key)) continue;
      seen.add(key);
      if (selected(p) || favorite(p, favorites)) pinned.push(p);
      else if (ids(p).some(id => viewed[id])) known.push(p);
      else fresh.push(p);
    }
    const result = [...pinned];
    for (let i = 0; i < Math.max(known.length, fresh.length); i++) {
      if (known[i]) result.push(known[i]);
      if (fresh[i]) result.push(fresh[i]);
    }
    return result;
  }
  function colorURL(href, color, pairs, base) {
    const url = new URL(href, base);
    if (url.origin !== new URL(base).origin) return url.href;
    const pair = pairs.find(p => p.gold === url.pathname || p.silver === url.pathname);
    if (pair && pair[color] && pair[color] !== url.pathname) {
      url.pathname = pair[color];
      url.searchParams.delete('page');
      for (const key of [...url.searchParams.keys()]) {
        if (/^filter\.v\.option\./.test(key) && /color|colour|couleur|finition|finish/i.test(key)) url.searchParams.delete(key);
      }
    }
    return url.href;
  }
  function cartCandidates(products, items, favorites = {}, history = {}) {
    const inCart = new Set(items.map(item => String(item.product_id)));
    const isChain = p => /chaine|chain|chaîne/i.test(`${p.title} ${p.type}`);
    const available = products.filter(p => p.available && !inCart.has(String(p.id)));
    const interested = available.filter(p => !isChain(p) && (favorite(p, favorites) || history[p.id]?.visits >= 2 || history[p.id]?.photos >= 2))
      .sort((a, b) => Number(favorite(b, favorites)) - Number(favorite(a, favorites)) || (history[b.id]?.lastAt || 0) - (history[a.id]?.lastAt || 0)).slice(0, 4);
    const compatible = new Set(products.filter(p => inCart.has(String(p.id))).flatMap(p => (p.chains || []).map(String)));
    const chains = available.filter(p => isChain(p) && compatible.has(String(p.id))).slice(0, 3);
    const primary = [];
    for (let i = 0; i < Math.max(interested.length, chains.length); i++) {
      if (interested[i]) primary.push({...interested[i], discountEligible: true});
      if (chains[i]) primary.push({...chains[i], discountEligible: false});
    }
    const selected = new Set(primary.map(p => String(p.id)));
    // A first visit still gets suggestions; discovery never qualifies for the hesitation offer.
    const fallback = available.filter(p => !isChain(p) && !selected.has(String(p.id))).slice(0, 12)
      .map(p => ({...p, discountEligible: false}));
    return {primary, fallback};
  }
  const api = {finish, ids, favorite, salesOrder, composerOrder, isStudLike, alternate, colorURL, cartCandidates};
  if (typeof module !== 'undefined') module.exports = api;
  else root.SolsticePersonalizationCore = api;
})(typeof window === 'undefined' ? globalThis : window);
